import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import { projectAPI } from '../services/api';
import useAuthStore from '../store/authStore';
import './GanttChart.css';

const GanttChart = () => {
  const { user } = useAuthStore();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('all');
  const [viewMode, setViewMode] = useState('month');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchGanttData();
  }, [selectedProject]);

  const fetchGanttData = async () => {
    try {
      setLoading(true);
      const params = selectedProject !== 'all' ? { projectId: selectedProject } : {};
      const response = await projectAPI.getGanttData(params);
      
      let allProjects = response.data.projects || [];
      let allTasks = response.data.data || [];
      
      if (user?.role === 'Project Lead') {
        allProjects = allProjects.filter(p => p.projectLead?._id === user.id);
        const supervisedProjectIds = allProjects.map(p => p._id);
        allTasks = allTasks.filter(t => supervisedProjectIds.includes(t.projectId));
      }
      
      setProjects(allProjects);
      setTasks(allTasks);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load Gantt chart data');
    } finally {
      setLoading(false);
    }
  };

  const filteredTasks = useMemo(() => {
    return tasks.filter(task => task.startDate && task.endDate);
  }, [tasks]);

  const { timelineStart, timelineEnd, totalDays } = useMemo(() => {
    if (filteredTasks.length === 0) {
      const today = new Date();
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const end = new Date(today.getFullYear(), today.getMonth() + 2, 0);
      return { 
        timelineStart: start, 
        timelineEnd: end, 
        totalDays: Math.ceil((end - start) / (1000 * 60 * 60 * 24)) 
      };
    }

    const dates = filteredTasks.flatMap(t => [new Date(t.startDate), new Date(t.endDate)]);
    const minDate = new Date(Math.min(...dates));
    const maxDate = new Date(Math.max(...dates));
    
    minDate.setDate(minDate.getDate() - 5);
    maxDate.setDate(maxDate.getDate() + 5);
    
    return {
      timelineStart: minDate,
      timelineEnd: maxDate,
      totalDays: Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24))
    };
  }, [filteredTasks]);

  const months = useMemo(() => {
    const monthsList = [];
    const current = new Date(timelineStart);
    
    while (current <= timelineEnd) {
      const monthStart = new Date(current);
      const monthEnd = new Date(current.getFullYear(), current.getMonth() + 1, 0);
      const endDate = monthEnd > timelineEnd ? timelineEnd : monthEnd;
      
      const daysInView = Math.ceil((endDate - (monthStart < timelineStart ? timelineStart : monthStart)) / (1000 * 60 * 60 * 24)) + 1;
      
      monthsList.push({
        label: current.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        days: daysInView,
        percentage: (daysInView / totalDays) * 100
      });
      
      current.setMonth(current.getMonth() + 1);
      current.setDate(1);
    }
    
    return monthsList;
  }, [timelineStart, timelineEnd, totalDays]);

  const getDaysBetween = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    return Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
  };

  const getStartOffset = (taskStart) => {
    const taskStartDate = new Date(taskStart);
    return Math.ceil((taskStartDate - timelineStart) / (1000 * 60 * 60 * 24));
  };

  const getProgressColor = (task) => {
    if (task.status === 'Done') return 'complete';
    if (task.status === 'Blocked') return 'blocked';
    if (task.progress >= 50) return 'on-track';
    if (task.progress > 0) return 'in-progress';
    return 'not-started';
  };

  const findCriticalPath = () => {
    if (filteredTasks.length === 0) return [];
    
    const taskMap = new Map(filteredTasks.map(t => [t.id.toString(), t]));
    const visited = new Set();
    const path = [];
    
    const dfs = (taskId) => {
      if (visited.has(taskId)) return 0;
      visited.add(taskId);
      
      const task = taskMap.get(taskId);
      if (!task) return 0;
      
      const taskDuration = getDaysBetween(task.startDate, task.endDate);
      let maxPath = 0;
      let nextTask = null;
      
      filteredTasks.forEach(t => {
        if (t.dependencies.some(d => d.id.toString() === taskId)) {
          const pathLength = dfs(t.id.toString());
          if (pathLength > maxPath) {
            maxPath = pathLength;
            nextTask = t;
          }
        }
      });
      
      if (nextTask) {
        path.push(nextTask);
      }
      
      return taskDuration + maxPath;
    };
    
    const rootTasks = filteredTasks.filter(t => t.dependencies.length === 0);
    let longestPath = [];
    let maxLength = 0;
    
    rootTasks.forEach(root => {
      visited.clear();
      path.length = 0;
      path.push(root);
      const length = dfs(root.id.toString());
      
      if (length > maxLength) {
        maxLength = length;
        longestPath = [...path];
      }
    });
    
    return longestPath;
  };

  const criticalPath = useMemo(() => findCriticalPath(), [filteredTasks]);

  if (loading) {
    return (
      <Layout>
        <div className="gantt-page">
          <div className="loading-state">Loading Gantt chart...</div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="gantt-page">
          <div className="error-state">{error}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="gantt-page">
        <div className="page-header">
          <div>
            <h1 className="page-title">
              <svg className="title-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              Gantt Chart
            </h1>
            <p className="page-subtitle">
              {user?.role === 'Project Lead' 
                ? 'Timeline view of tasks from your supervised projects'
                : 'Project timeline and task dependencies'}
            </p>
          </div>
          <div className="gantt-filters">
            <div className="view-mode-toggle">
              <button 
                className={`view-btn ${viewMode === 'month' ? 'active' : ''}`}
                onClick={() => setViewMode('month')}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                Month
              </button>
              <button 
                className={`view-btn ${viewMode === 'week' ? 'active' : ''}`}
                onClick={() => setViewMode('week')}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                </svg>
                Week
              </button>
            </div>
            <select 
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Projects</option>
              {projects.map(project => (
                <option key={project._id} value={project._id}>{project.name}</option>
              ))}
            </select>
          </div>
        </div>

        {filteredTasks.length === 0 ? (
          <div className="empty-state">
            <p>No tasks with dates found. Please add tasks with start and end dates to view the Gantt chart.</p>
          </div>
        ) : (
          <>
            <div className="gantt-container">
              <div className="gantt-sidebar">
                <div className="gantt-header-cell">Task Name</div>
                {filteredTasks.map(task => (
                  <div key={task.id} className="gantt-task-name">
                    <span className="task-name-text" title={task.title}>{task.title}</span>
                    <span className="task-assignee">{task.assignee}</span>
                  </div>
                ))}
              </div>

              <div className="gantt-timeline">
                <div className="gantt-timeline-header">
                  {months.map((month, idx) => (
                    <div 
                      key={idx} 
                      className="timeline-month" 
                      style={{ width: `${month.percentage}%` }}
                    >
                      {month.label}
                    </div>
                  ))}
                </div>
                <div className="gantt-grid">
                  {filteredTasks.map(task => {
                    const startOffset = getStartOffset(task.startDate);
                    const duration = getDaysBetween(task.startDate, task.endDate);
                    const leftPercent = (startOffset / totalDays) * 100;
                    const widthPercent = (duration / totalDays) * 100;
                    const isCritical = criticalPath.some(t => t.id === task.id);
                    
                    return (
                      <div key={task.id} className="gantt-row">
                        <div 
                          className={`gantt-bar ${getProgressColor(task)} ${isCritical ? 'critical' : ''}`}
                          style={{
                            left: `${leftPercent}%`,
                            width: `${widthPercent}%`
                          }}
                          title={`${task.title}\nStatus: ${task.status}\nProgress: ${task.progress}%\nDuration: ${duration} days`}
                        >
                          <div className="gantt-bar-progress" style={{width: `${task.progress}%`}}></div>
                          <span className="gantt-bar-label">{task.progress}%</span>
                          {isCritical && <span className="critical-badge">!</span>}
                        </div>
                        {task.dependencies.length > 0 && task.dependencies.map(dep => {
                          const depTask = filteredTasks.find(t => t.id === dep.id);
                          if (!depTask) return null;
                          
                          const depEndOffset = getStartOffset(depTask.endDate);
                          const taskStartOffset = getStartOffset(task.startDate);
                          
                          return (
                            <svg key={dep.id} className="dependency-line" style={{ pointerEvents: 'none' }}>
                              <line
                                x1={`${(depEndOffset / totalDays) * 100}%`}
                                y1="30px"
                                x2={`${(taskStartOffset / totalDays) * 100}%`}
                                y2="30px"
                                stroke="#6366f1"
                                strokeWidth="2"
                                markerEnd="url(#arrowhead)"
                              />
                            </svg>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="gantt-stats">
              <div className="stat-card">
                <svg className="stat-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                </svg>
                <div className="stat-content">
                  <div className="stat-value">{filteredTasks.length}</div>
                  <div className="stat-label">Total Tasks</div>
                </div>
              </div>
              <div className="stat-card">
                <svg className="stat-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                <div className="stat-content">
                  <div className="stat-value">{filteredTasks.filter(t => t.status === 'Done').length}</div>
                  <div className="stat-label">Completed</div>
                </div>
              </div>
              <div className="stat-card">
                <svg className="stat-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
                <div className="stat-content">
                  <div className="stat-value">{filteredTasks.filter(t => t.status === 'In Progress').length}</div>
                  <div className="stat-label">In Progress</div>
                </div>
              </div>
              <div className="stat-card">
                <svg className="stat-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
                <div className="stat-content">
                  <div className="stat-value">{filteredTasks.filter(t => t.status === 'Blocked').length}</div>
                  <div className="stat-label">Blocked</div>
                </div>
              </div>
            </div>

            <div className="gantt-legend">
              <h3>
                <svg className="legend-title-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="16" x2="12" y2="12"/>
                  <line x1="12" y1="8" x2="12.01" y2="8"/>
                </svg>
                Legend
              </h3>
              <div className="legend-items">
                <div className="legend-item">
                  <div className="legend-color complete"></div>
                  <span>Complete (100%)</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color on-track"></div>
                  <span>On Track (50-99%)</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color in-progress"></div>
                  <span>In Progress (1-49%)</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color not-started"></div>
                  <span>Not Started (0%)</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color blocked"></div>
                  <span>Blocked</span>
                </div>
              </div>
            </div>

            {criticalPath.length > 0 && (
              <div className="critical-path">
                <h3>
                  <svg className="critical-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/>
                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                  Critical Path Analysis
                </h3>
                <p className="critical-tasks">Tasks on critical path: {criticalPath.map(t => t.title).join(' → ')}</p>
                <p className="warning-text">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  Any delay in these tasks will delay the entire project
                </p>
              </div>
            )}
          </>
        )}

        <svg width="0" height="0">
          <defs>
            <marker id="arrowhead" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
              <polygon points="0 0, 10 3, 0 6" fill="#6366f1" />
            </marker>
          </defs>
        </svg>
      </div>
    </Layout>
  );
};

export default GanttChart;
